import { useState, useEffect } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { useViewport } from '../../hooks/useViewport';
import { unifiedApiService } from '../../services/unified';
import { UserDashboardLayout } from '../../components/layout/UserDashboardLayout';
import { 
  ProfileForm, 
  ExperienceSection, 
  EducationSection, 
  PreferencesSection 
} from '../../components/profile';
import { LoadingSpinner } from '../../components/layout/LoadingSpinner';
import type { Profile } from '../../types/profile';

export function ProfilePage() {
  const { } = useAuth();
  useViewport(); // Used for responsive behavior via CSS
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'profile' | 'experience' | 'education' | 'preferences'>('profile');

  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    try {
      setLoading(true);
      setError(null);
      const profileData = await unifiedApiService.profile.getProfile();
      setProfile(profileData.profile as any);
    } catch (err) {
      const error = err as Error;
      setError(error.message || 'Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const handleProfileUpdate = (updatedProfile: Profile) => {
    setProfile(updatedProfile);
  };

  if (loading) {
    return (
      <UserDashboardLayout>
        <div className="flex items-center justify-center h-screen">
          <LoadingSpinner size="lg" />
        </div>
      </UserDashboardLayout>
    );
  }

  if (error) {
    return (
      <UserDashboardLayout>
        <div className="flex items-center justify-center h-screen">
          <div className="text-center">
            <div className="text-red-600 mb-4">
              <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 18.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">Error Loading Profile</h3>
            <p className="text-gray-600 mb-4">{error}</p>
            <button
              onClick={loadProfile}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors"
            >
              Try Again
            </button>
          </div>
        </div>
      </UserDashboardLayout>
    );
  }

  const tabs = [
    { id: 'profile', name: 'Profile', icon: 'user' },
    { id: 'experience', name: 'Experience', icon: 'briefcase' },
    { id: 'education', name: 'Education', icon: 'academic-cap' },
    { id: 'preferences', name: 'Preferences', icon: 'cog' },
  ] as const;

  return (
    <UserDashboardLayout>
      <div className="p-4 sm:p-6">
        {/* Header - Mobile optimized */}
        <div className="mb-4 sm:mb-6">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">Profile Management</h1>
          <p className="text-sm sm:text-base text-gray-600">
            Upload and manage your resumes. Our AI will analyze them to help match you with the best opportunities.
          </p>
        </div>

        {/* Tab Navigation - Scrollable on mobile */}
        <div className="border-b border-gray-200 mb-4 sm:mb-6 -mx-4 sm:mx-0 px-4 sm:px-0">
          <nav className="-mb-px flex space-x-4 sm:space-x-8 overflow-x-auto mobile-scroll-x pb-px">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-2.5 sm:py-2 px-1 border-b-2 font-medium text-sm transition-colors whitespace-nowrap min-h-[44px] ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab.name}
              </button>
            ))}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="bg-white shadow rounded-lg">
          {activeTab === 'profile' && (
            <ProfileForm
              profile={profile}
              onUpdate={handleProfileUpdate}
            />
          )}
          {activeTab === 'experience' && (
            <ExperienceSection />
          )}
          {activeTab === 'education' && (
            <EducationSection />
          )}
          {activeTab === 'preferences' && (
            <PreferencesSection
              profile={profile}
              onUpdate={handleProfileUpdate}
            />
          )}
        </div>
      </div>
    </UserDashboardLayout>
  );
}